/**
 * Creating a business from an invite.
 *
 * `plan-documents/PLAN_tenant_signup_20260919.md` §4 is the flow, §5 the
 * refusals. **The routes behind this are mounted only when `SIGNUP_ENABLED` is
 * true, and it is false in production** until `bank_connections` exists — a
 * second tenant stops the Mercury sync for everyone until then.
 *
 * ---------------------------------------------------------------------------
 * THE KEY IS A STEP OF ITS OWN, AND THAT IS THE WHOLE POINT
 * ---------------------------------------------------------------------------
 * The operator's decision, and a better form than the one it replaced: an
 * unauthenticated page asking for a business name, a timezone and a fiscal
 * year — showing a derived storage slug as you type — tells a scanner more
 * about this application's architecture than the login page does.
 *
 * So **nothing about the system's shape is rendered before the key validates**.
 * No timezone list, no fiscal-year options, no password rules, no slug. That
 * rule has to survive later convenience, and
 * `apps/web/test/SignupPage.test.tsx` fails if the second step's fields appear
 * on the first.
 *
 * ---------------------------------------------------------------------------
 * NOTHING IS WRITTEN UNTIL THE LAST STEP, AND THE SCREEN SAYS SO
 * ---------------------------------------------------------------------------
 * A person cannot infer it, and it is the only thing that makes abandoning
 * halfway safe: the invite is still good, and no half-built business is left
 * behind. Said in the second and third steps, where somebody might otherwise
 * be afraid to close the tab.
 *
 * ---------------------------------------------------------------------------
 * THE TIME ZONE HAS NO DEFAULT
 * ---------------------------------------------------------------------------
 * Migration 0006 removed it deliberately, and guessing from the browser and
 * being one zone wrong is precisely the bug that cost five months of bank
 * reconciliation. An empty select that must be chosen is the correct rudeness.
 */

import { useState, type FormEvent } from 'react';
import { ApiError, api, type BegunSignup, type BusinessTypeChoice } from '../api.js';
import { RecoveryCodes } from '../components/RecoveryCodes.js';
import { availableTimeZones } from '../lib/timezone.js';
import { InfoNote } from '../components/InfoNote.js';
import {
  LEGAL_FORMS,
  LEGAL_FORM_NAMES,
  TAX_STATUS_NAMES,
  defaultNotBuilt,
  electionFor,
  offeredTaxStatuses,
  preselectedTaxStatus,
  recommendedTaxStatus,
  type ChosenLegalForm,
} from '../lib/business-types.js';
import { reloadTo } from '../lib/reload.js';

type Step = 'key' | 'details' | 'code' | 'codes';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function messageFor(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    if (error.code === 'network_unreachable') {
      return 'Cannot reach the server. Check your connection and try again.';
    }
    return error.message === error.code ? fallback : error.message;
  }
  return fallback;
}

export function SignupPage() {
  const [step, setStep] = useState<Step>('key');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [code, setCode] = useState('');
  const [email, setEmail] = useState('');
  /*
   * The invited address already signs in (issue #50): the business is added to
   * that login, so the next steps ask for ITS password and ITS current code
   * rather than enrolling new ones, and no recovery codes are issued.
   */
  const [existingLogin, setExistingLogin] = useState(false);

  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState('');
  const [fiscalMonth, setFiscalMonth] = useState('1');
  // What kind of business (0078, 0094): the legal form, then how it is taxed.
  // No default form: a guess is the bug. The IRS default for the form chosen is
  // preselected where its rules are built, and only there (round 181's TX-2).
  const [legalForm, setLegalForm] = useState<'' | ChosenLegalForm>('');
  const [taxStatus, setTaxStatus] = useState<'' | BusinessTypeChoice['taxStatus']>('');
  const [foundationStatus, setFoundationStatus] = useState<'' | NonNullable<BusinessTypeChoice['foundationStatus']>>('');
  const [annualReturn, setAnnualReturn] = useState<'' | NonNullable<BusinessTypeChoice['annualReturn']>>('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  const [enrolment, setEnrolment] = useState<BegunSignup | null>(null);
  const [totp, setTotp] = useState('');
  const [issued, setIssued] = useState<string[] | null>(null);

  async function run(work: () => Promise<void>, fallback: string) {
    setBusy(true);
    setError(null);
    try {
      await work();
    } catch (caught) {
      setError(messageFor(caught, fallback));
    } finally {
      setBusy(false);
    }
  }

  const submitKey = (event: FormEvent) => {
    event.preventDefault();
    void run(async () => {
      const checked = await api.signupKey(code.trim());
      setEmail(checked.email);
      setExistingLogin(checked.existingLogin);
      setStep('details');
    }, 'That key is not valid.');
  };

  const submitDetails = (event: FormEvent) => {
    event.preventDefault();
    // Checked here because the server never sees `confirm` — it is a typing
    // guard, not a rule about the account, and it is the one refusal this
    // screen is entitled to make on its own.
    if (!existingLogin && password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }
    void run(async () => {
      setEnrolment(
        await api.signupBegin({
          code: code.trim(),
          name: name.trim(),
          timezone,
          fiscalYearStartMonth: Number(fiscalMonth),
          password,
          // The API checks the combination and says what is missing in a sentence.
          businessType: {
            taxStatus: taxStatus as BusinessTypeChoice['taxStatus'],
            legalForm: legalForm as ChosenLegalForm,
            ...(foundationStatus ? { foundationStatus } : {}),
            ...(annualReturn ? { annualReturn } : {}),
          },
        }),
      );
      setStep('code');
    }, 'Could not check those details.');
  };

  const submitCode = (event: FormEvent) => {
    event.preventDefault();
    if (!enrolment) return;
    void run(async () => {
      const created = await api.signupComplete(enrolment.token, totp.trim());
      // The password is no longer needed anywhere on this screen.
      setPassword('');
      setConfirm('');
      // Joining an existing login issues no codes: it keeps the ones it has.
      if (created.codes.length === 0) {
        reloadTo('/');
        return;
      }
      setIssued(created.codes);
      setStep('codes');
    }, 'Could not finish setting up the business.');
  };

  return (
    <main className="centered">
      <h1>Techne Books</h1>

      {step === 'key' && (
        <form onSubmit={submitKey}>
          <p className="muted">Enter the key you were given.</p>
          <label htmlFor="signup-key">Key</label>
          <input
            id="signup-key"
            autoComplete="off"
            autoFocus
            required
            value={code}
            onChange={(event) => setCode(event.target.value)}
            disabled={busy}
          />
          {error && <p role="alert" className="error">{error}</p>}
          <button type="submit" disabled={busy}>
            {busy ? 'Checking…' : 'Continue'}
          </button>
        </form>
      )}

      {step === 'details' && (
        <form onSubmit={submitDetails}>
          {/*
            * The address is SHOWN, not asked for. It comes from the invite, so
            * the mismatch case cannot happen and there is nothing to mistype.
            */}
          {existingLogin ? (
            <p className="muted">
              <strong>{email}</strong> already signs in to a business. This one will be
              added to it: enter that password here, and the next step asks for a code
              from the authenticator you sign in with.
            </p>
          ) : (
            <p className="muted">
              Setting up the books for <strong>{email}</strong>.
            </p>
          )}
          <p className="muted">Nothing is saved until the last step.</p>

          <label htmlFor="signup-name">Business Name</label>
          <input
            id="signup-name"
            required
            value={name}
            onChange={(event) => setName(event.target.value)}
            disabled={busy}
          />

          <label htmlFor="signup-zone">Business Time Zone</label>
          <select
            id="signup-zone"
            required
            value={timezone}
            onChange={(event) => setTimezone(event.target.value)}
            disabled={busy}
          >
            {/*
              * NO DEFAULT, and the empty option is why. Which zone a business
              * keeps its books in decides which month a transaction lands in,
              * and a guess that is one zone wrong is silently wrong forever.
              */}
            <option value="">Choose one…</option>
            {availableTimeZones().map((zone) => (
              <option key={zone} value={zone}>{zone}</option>
            ))}
          </select>

          <label htmlFor="signup-fiscal">Fiscal Year Starts</label>
          <select
            id="signup-fiscal"
            value={fiscalMonth}
            onChange={(event) => setFiscalMonth(event.target.value)}
            disabled={busy}
          >
            {MONTHS.map((month, index) => (
              <option key={month} value={String(index + 1)}>{month}</option>
            ))}
          </select>

          <label htmlFor="signup-legal-form">Legal Form</label>
          <select
            id="signup-legal-form"
            required
            value={legalForm}
            onChange={(event) => {
              const next = event.target.value as typeof legalForm;
              setLegalForm(next);
              setTaxStatus(next === '' ? '' : (preselectedTaxStatus(next) ?? ''));
              setFoundationStatus('');
              setAnnualReturn('');
            }}
            disabled={busy}
          >
            {/* No default: each form is taxed its own ways, and the books follow the tax (0094). */}
            <option value="">Choose one…</option>
            {LEGAL_FORMS.map((form) => (
              <option key={form} value={form}>{LEGAL_FORM_NAMES[form]}</option>
            ))}
          </select>

          {legalForm !== '' && (
            <>
              <label htmlFor="signup-taxed-as">Taxed As</label>
              <select
                id="signup-taxed-as"
                required
                value={taxStatus}
                onChange={(event) => {
                  setTaxStatus(event.target.value as typeof taxStatus);
                  setFoundationStatus('');
                  setAnnualReturn('');
                }}
                disabled={busy}
              >
                {preselectedTaxStatus(legalForm) === null && <option value="">Choose one…</option>}
                {offeredTaxStatuses(legalForm).map((status) => (
                  <option key={status} value={status}>
                    {TAX_STATUS_NAMES[status]}
                    {status === recommendedTaxStatus(legalForm) ? ' (Recommended)' : ''}
                  </option>
                ))}
              </select>
              {defaultNotBuilt(legalForm) !== null && <p className="muted">{defaultNotBuilt(legalForm)}</p>}
              {taxStatus !== '' && taxStatus === recommendedTaxStatus(legalForm) && offeredTaxStatuses(legalForm).length > 1 && (
                <p className="muted">Recommended: the IRS default for a {LEGAL_FORM_NAMES[legalForm].toLowerCase().replace('llc', 'LLC')}.</p>
              )}
              {/* The default's sentence names no election; this names the one the choice means (TT-1, 0095). */}
              {taxStatus !== '' && electionFor(legalForm, taxStatus) !== null && <p className="muted">{electionFor(legalForm, taxStatus)}</p>}
              {taxStatus === 'disregarded_entity' && fiscalMonth !== '1' && (
                <p className="muted">
                  A business taxed with its owner keeps the calendar year, since its income goes on the owner's own return: its
                  fiscal year starts in January.
                </p>
              )}
              <InfoNote label="How a business is taxed">
                <p>
                  The IRS taxes each legal form one way unless the business filed an election. A single-member LLC is taxed
                  with its owner, on the owner's own return; a multi-member LLC as a partnership; a corporation as a C
                  corporation. Form 2553 makes any of them an S corporation, and Form 8832 makes an LLC a corporation.
                </p>
                <p>
                  The books follow the tax: the owner's accounts, who may be on payroll, the year-end close and which return
                  is due. Choose what the business actually files, and ask the CPA if unsure. It cannot be changed here once
                  anything is posted.
                </p>
              </InfoNote>
            </>
          )}

          {taxStatus === 'tax_exempt' && (
            <>
              <label htmlFor="signup-foundation">Foundation Status</label>
              <select
                id="signup-foundation"
                required
                value={foundationStatus}
                onChange={(event) => {
                  setFoundationStatus(event.target.value as typeof foundationStatus);
                  setAnnualReturn('');
                }}
                disabled={busy}
              >
                <option value="">Choose one…</option>
                <option value="public_charity">Public Charity</option>
                <option value="private_operating_foundation">Private Operating Foundation</option>
                <option value="private_nonoperating_foundation">Private Non-Operating Foundation</option>
              </select>
              {foundationStatus === 'public_charity' && (
                <>
                  <label htmlFor="signup-return">Annual Return</label>
                  <select
                    id="signup-return"
                    required
                    value={annualReturn}
                    onChange={(event) => setAnnualReturn(event.target.value as typeof annualReturn)}
                    disabled={busy}
                  >
                    <option value="">Choose one…</option>
                    <option value="990_n">Form 990-N</option>
                    <option value="990_ez">Form 990-EZ</option>
                    <option value="990">Form 990</option>
                  </select>
                </>
              )}
              {foundationStatus !== '' && foundationStatus !== 'public_charity' && (
                <p className="muted">A private foundation files Form 990-PF each year, whatever its size.</p>
              )}
            </>
          )}

          <label htmlFor="signup-password">{existingLogin ? 'Your Password' : 'Password'}</label>
          <input
            id="signup-password"
            type="password"
            autoComplete={existingLogin ? 'current-password' : 'new-password'}
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            disabled={busy}
          />

          {!existingLogin && (
            <>
              <label htmlFor="signup-confirm">Confirm Password</label>
              <input
                id="signup-confirm"
                type="password"
                autoComplete="new-password"
                required
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                disabled={busy}
              />
            </>
          )}

          {error && <p role="alert" className="error">{error}</p>}
          <button type="submit" disabled={busy}>
            {busy ? 'Checking…' : 'Continue'}
          </button>
        </form>
      )}

      {step === 'code' && enrolment && (
        <form onSubmit={submitCode}>
          {enrolment.existingLogin ? (
            <p className="muted">
              Enter the code from the authenticator you sign in with as {email}.
            </p>
          ) : (
            <>
              <p className="muted">
                Add this account to an authenticator app, then enter the code it shows.
                Signing in needs it every time, so put it somewhere you will keep.
              </p>
              <p className="muted">
                On this device, open <a href={enrolment.otpauthUri ?? undefined}>the authenticator link</a>.
                On another, add it by hand with this key:
              </p>
              <p><code className="enrolment-secret">{enrolment.secret}</code></p>
            </>
          )}
          <p className="muted">
            <strong>Nothing has been saved yet.</strong> The business is created when this
            code is accepted, and your key is still good until then.
          </p>

          <label htmlFor="signup-totp">Six-digit code</label>
          <input
            id="signup-totp"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            required
            value={totp}
            onChange={(event) => setTotp(event.target.value)}
            disabled={busy}
          />

          {error && <p role="alert" className="error">{error}</p>}
          <button type="submit" disabled={busy}>
            {busy ? 'Setting up…' : enrolment.existingLogin ? 'Add The Business' : 'Create The Business'}
          </button>
        </form>
      )}

      {step === 'codes' && issued && (
        <RecoveryCodes
          codes={issued}
          reason="created"
          // A full reload rather than a route change: the session cookie was
          // set by the request that created the account, and the shell reads
          // the session once on mount.
          onDone={() => { window.location.assign('/'); }}
        />
      )}

      <p className="muted">
        <a href="/privacy">Privacy Policy</a>
      </p>
    </main>
  );
}
